import { escapeRegExp } from './helper';
import {
  BooleanFilterItem,
  BooleanFilterOptions,
  DateFilterItem,
  DateFilterOptions,
  DcuplQuery,
  DcuplQueryGroup,
  DcuplQueryStatement,
  NumberFilterItem,
  NumberFilterOptions,
  ReferenceFilterItem,
  ReferenceFilterOptions,
  TextFilterItem,
  TextFilterOptions,
} from './types';

export const generateTextQuery = (options: TextFilterOptions, filterItem?: TextFilterItem): DcuplQueryGroup => {
  const escapedText = escapeRegExp(options.text || '');

  const attribute = filterItem?.attributeKey || options.filterKey;

  let query!: DcuplQueryGroup<DcuplQuery>;

  const comparisonType = filterItem?.comparisonType || 'containsIgnoreCase';

  if (comparisonType === 'containsIgnoreCase') {
    query = {
      groupKey: options.filterKey,
      queries: [
        {
          queryKey: options.text,
          attribute: attribute,
          value: `/${escapedText}/`,
          operator: 'find',
          options: {
            transform: ['lowercase'],
          },
        },
      ],
    };
  } else if (comparisonType === 'containsIgnoreCaseAndWhitespace') {
    query = {
      groupKey: options.filterKey,
      queries: [
        {
          queryKey: options.text,
          attribute: attribute,
          value: `/${escapedText}/`,
          operator: 'find',
          options: {
            transform: ['removeWhitespace', 'lowercase'],
          },
        },
      ],
    };
  } else if (comparisonType === 'contains') {
    query = {
      groupKey: options.filterKey,
      queries: [
        {
          queryKey: options.text,
          attribute: attribute,
          value: `/${escapedText}/`,
          operator: 'find',
        },
      ],
    };
  } else if (comparisonType === 'equal') {
    query = {
      groupKey: options.filterKey,
      queries: [
        {
          queryKey: options.text,
          attribute: attribute,
          value: options.text,
          operator: 'eq',
        },
      ],
    };
  } else if (comparisonType === 'equalIgnoreCase') {
    query = {
      groupKey: options.filterKey,
      queries: [
        {
          queryKey: options.text,
          attribute: attribute,
          value: options.text,
          operator: 'eq',
          options: {
            transform: ['lowercase'],
          },
        },
      ],
    };
  }

  return query;
};

export const generateBooleanQuery = (
  options: BooleanFilterOptions,
  filterItem?: BooleanFilterItem,
): DcuplQueryGroup => {
  const attribute = filterItem?.attributeKey || options.filterKey;

  const query: DcuplQueryGroup = {
    groupKey: options.filterKey,
    queries: [
      {
        queryKey: options.value,
        attribute: attribute,
        value: options.value,
        operator: 'eq',
      },
    ],
  };
  return query;
};

export const generateNumberAndDateQuery = (
  options: NumberFilterOptions | DateFilterOptions,
  filterItem?: NumberFilterItem | DateFilterItem,
): DcuplQueryGroup => {
  const attribute = filterItem?.attributeKey || options.filterKey;
  const query: DcuplQueryGroup = {
    groupKey: options.filterKey,
    queries: [],
  };

  if (typeof options.eq !== 'undefined') {
    query.groupType = 'or';
    if (Array.isArray(options.eq)) {
      options.eq.forEach((value) => {
        query.queries.push({ attribute: attribute, value: value, operator: 'eq' });
      });
    } else {
      query.queries.push({ attribute: attribute, value: options.eq, operator: 'eq' });
    }
  }
  if (typeof options.ne !== 'undefined') {
    query.groupType = 'and';
    if (Array.isArray(options.ne)) {
      options.ne.forEach((value) => {
        query.queries.push({ attribute: attribute, value: value, operator: 'eq', options: { invert: true } });
      });
    } else {
      query.queries.push({
        attribute: attribute,
        value: options.ne,
        operator: 'eq',
        options: { invert: true },
      });
    }
  }
  if (typeof options.gt !== 'undefined') {
    query.groupType = 'and';
    query.queries.push({ attribute: attribute, value: options.gt, operator: 'gt' });
  }
  if (typeof options.lt !== 'undefined') {
    query.groupType = 'and';
    query.queries.push({ attribute: attribute, value: options.lt, operator: 'lt' });
  }
  if (typeof options.gte !== 'undefined') {
    query.groupType = 'and';
    query.queries.push({ attribute: attribute, value: options.gte, operator: 'gte' });
  }
  if (typeof options.lte !== 'undefined') {
    query.groupType = 'and';
    query.queries.push({ attribute: attribute, value: options.lte, operator: 'lte' });
  }

  return query;
};

export const generateReferenceQuery = (
  options: ReferenceFilterOptions,
  filterItem?: ReferenceFilterItem,
): DcuplQueryGroup => {
  const attribute = filterItem?.attributeKey || options.filterKey;
  const query: DcuplQueryGroup = {
    groupKey: options.filterKey,
    queries: [],
  };

  options.itemKeys.forEach((key) => {
    query.queries.push({
      queryKey: key,
      attribute: attribute,
      value: { key: key },
      operator: 'eq',
    });
  });

  return query;
};

export const isValidQueryStatement = (query: DcuplQueryStatement): boolean => {
  if (typeof query.modelKey !== 'string') return false;

  if (!Array.isArray(query.queries)) return false;

  return true;
};
